import styles from './Admin.module.css';

import { useState, useEffect, useMemo } from 'react';

import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Icon from '@mui/material/Icon';
import TablePagination from '@mui/material/TablePagination';
import Typography from '@mui/material/Typography';

import Group from '@mui/icons-material/Group';

import { getAllUsers, updateUser } from '../../api.js';
import { useAuth } from '../../AuthContext';

import { User } from '../User/User';
import { NewUser } from '../NewUser/NewUser';
import { Filter } from '../Filter/Filter';

export const Admin = ({ size }) => {
    const [ error, setError ] = useState(null);
    const [ loading, setLoading ] = useState(true);
    const [ onNewUser, setOnNewUser ] = useState(false);
    const [ users, setUsers ] = useState([]);
    const [ page, setPage ] = useState(0);
    const [ rowsPerPage, setRowsPerPage ] = useState(size || 10);
    const [ filters, setFilters ] = useState({ search: '' });

    const { token } = useAuth();

    const rowsPerPageOptions = useMemo(() => {
        const options = [ 10, 25, 50, 100 ];

        return size ? options.filter(option => option <= size) : options;
    }, [ size ]);

    const pageSize = size ? Math.min(rowsPerPage, size) : rowsPerPage;

    // Memoize the sorted users to avoid re-sorting on every render
    const sortedUsers = useMemo(() => {
        // users are sorted when the API returns
        return users.filter(user => {
            if (filters.search && filters.search.trim() !== '') {
                const searchLower = filters.search.toLowerCase();

                const matchesName = user.firstName?.toLowerCase().includes(searchLower) ||
                    user.lastName?.toLowerCase().includes(searchLower) ||
                    user.displayName?.toLowerCase().includes(searchLower);

                const matchesEmail = user.email?.toLowerCase().includes(searchLower);
                const matchesPhone = user.phoneNumber?.toLowerCase().includes(searchLower);

                const matchesVendor = user.vendorName?.toLowerCase().includes(searchLower);

                if (!(matchesName || matchesEmail || matchesPhone || matchesVendor)) {
                    return false;
                }
            }

            return true;
        });
    }, [ users, filters ]);

    // Memoize the current page data
    const currentPageData = useMemo(() => {
        const pageIndex = page;

        return sortedUsers.slice(pageIndex * pageSize, (pageIndex * pageSize) + pageSize);
    }, [ sortedUsers, page, pageSize ]);

    // Memoize pagination helpers
    const paginationInfo = useMemo(() => {
        const totalPages = Math.ceil(sortedUsers.length / pageSize);

        return { totalPages };
    }, [ sortedUsers.length, pageSize ]);

    // MARK: handlers
    const handleOnSave = async (user) => {
        try {
            const updated = await updateUser(user, token);

            if (updated.error) {
                setError(updated.error);
            } else {
                const updatedUsers = users.map(u => u.id === user.id ? user : u);

                setUsers(updatedUsers);
            }
        } catch (error) {
            setError(error.message);
        }
    };

    const handleFilterChange = (newFilters) => {
        setFilters(newFilters);
        setPage(0); // Reset to first page when filters change
    };

    const handleClearFilters = () => {
        setFilters({ search: '', days: [], onlyAvailable: false });
        setPage(0);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleNewUser = (newUser) => {
        setUsers(prev => [ ...prev, newUser ]);

        setOnNewUser(true);
    };

    // MARK: effects
    useEffect(() => {
        const fetchUsers = async () => {
            const result = await getAllUsers(token);

            if (result && !result.error) {
                setUsers(result);
            }

            if (result.error) {
                setError(result.error);
            }

            setLoading(false);
        };

        fetchUsers();
    }, [ token ]);

    if (loading) {
        return <CircularProgress />;
    }

    if (error) {
        return <Alert severity="error">{ error }</Alert>;
    }

    // MARK: render
    return (
        <div className={ styles.dashboard }>
            { onNewUser &&
                <Alert variant='filled' severity="success" style={ { marginBottom: '16px' } } onClose={ () => setOnNewUser(false) }>
                    New user added successfully!
                </Alert>
            }

            <NewUser onNewUser={ handleNewUser } />

            <Filter
                filters={ filters }
                onFilterChange={ handleFilterChange }
                onClearFilters={ handleClearFilters }
            />

            <div className={ styles.container }>
                <div className={ styles.titleBar }>
                    <span>
                        <Icon component={ Group } sx={ { mr: 1 } } />
                        <Typography>All Users</Typography>
                    </span>

                    { paginationInfo.totalPages > 1 && <TablePagination
                        component="div"
                        color="primary"
                        count={ sortedUsers.length }
                        page={ page }
                        onPageChange={ (event, value) => setPage(value) }
                        rowsPerPage={ rowsPerPage }
                        rowsPerPageOptions={ rowsPerPageOptions }
                        onRowsPerPageChange={ handleChangeRowsPerPage }
                    /> }
                </div>

                <div className={ styles.content }>
                    { currentPageData.map(f => {
                        return (
                            <User key={ f.id } user={ f } onSave={ handleOnSave } />
                        );
                    }) }
                </div>

                { paginationInfo.totalPages > 1 && <div className={ styles.footer }>
                    <TablePagination
                        component="div"
                        color="primary"
                        count={ sortedUsers.length }
                        page={ page }
                        onPageChange={ (event, value) => setPage(value) }
                        rowsPerPage={ rowsPerPage }
                        rowsPerPageOptions={ rowsPerPageOptions }
                        onRowsPerPageChange={ handleChangeRowsPerPage }
                    />
                </div> }
            </div>
        </div>
    );
};